import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Chip, Link as MuiLink, alpha, Select, MenuItem, FormControl, Button } from '@mui/material';
import { Article as ArticleIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

// Helper function to get provider badge color
const getProviderColor = (providerName) => {
  const colors = {
    'DuckDuckGo': { bg: alpha('#4285f4', 0.1), text: '#1565c0', border: alpha('#4285f4', 0.3) },
    'Google': { bg: alpha('#ea4335', 0.1), text: '#c62828', border: alpha('#ea4335', 0.3) },
    'RSS': { bg: alpha('#ff9800', 0.1), text: '#e65100', border: alpha('#ff9800', 0.3) },
    'NewsAPI': { bg: alpha('#9c27b0', 0.1), text: '#6a1b9a', border: alpha('#9c27b0', 0.3) },
    'Unknown': { bg: alpha('#9e9e9e', 0.08), text: '#757575', border: alpha('#9e9e9e', 0.2) }
  };
  return colors[providerName] || { bg: alpha('#000', 0.04), text: '#666', border: alpha('#000', 0.12) };
};

const RecentArticlesList = ({ articles, limit, onLimitChange, providers = [], selectedProvider = 'all', onProviderChange }) => {
  const { t } = useTranslation();
  const [displayCount, setDisplayCount] = useState(20);

  // Reset display count when articles change
  useEffect(() => {
    setDisplayCount(20);
  }, [articles]);

  const handleLoadMore = () => {
    setDisplayCount(prev => Math.min(prev + 20, articles.length));
  };

  const displayedArticles = articles.slice(0, displayCount);
  const hasMore = displayCount < articles.length;

  return (
    <Box>
      <Paper 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box 
          px={2.5}
          py={1.5}
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          sx={{
            bgcolor: alpha('#000', 0.02),
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              fontSize: '0.95rem',
              letterSpacing: -0.2
            }}
          >
            {t('aiNews.recentArticles')}
          </Typography>
          
          <Box display="flex" gap={1.5} alignItems="center">
            <Chip 
              label={articles.length}
              size="small"
              sx={{ 
                height: 22,
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor: 'primary.main',
                color: 'white',
                minWidth: 32
              }}
            />
            
            {/* Provider Filter */}
            {onProviderChange && providers.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedProvider}
                  onChange={(e) => onProviderChange(e.target.value)}
                  sx={{
                    height: 28,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: 'background.paper',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.12)
                    }
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>
                    {t('aiNews.allProviders')} ({providers.reduce((sum, p) => sum + p.article_count, 0)})
                  </MenuItem>
                  {providers.map(provider => (
                    <MenuItem key={provider.name} value={provider.name} sx={{ fontSize: '0.75rem' }}>
                      {provider.name} ({provider.article_count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {/* Limit Filter */}
            {onLimitChange && (
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={limit}
                  onChange={(e) => onLimitChange(e.target.value)}
                  sx={{
                    height: 28,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: 'background.paper',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.12)
                    }
                  }}
                >
                  <MenuItem value={10} sx={{ fontSize: '0.75rem' }}>10</MenuItem>
                  <MenuItem value={20} sx={{ fontSize: '0.75rem' }}>20</MenuItem>
                  <MenuItem value={30} sx={{ fontSize: '0.75rem' }}>30</MenuItem>
                  <MenuItem value={50} sx={{ fontSize: '0.75rem' }}>50</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* Content */}
        <Box p={2} sx={{ maxHeight: 600, overflowY: 'auto' }}>
          {articles.length === 0 ? (
            <Box 
              py={6} 
              textAlign="center"
              sx={{
                bgcolor: alpha('#000', 0.015),
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider'
              }}
            >
              <ArticleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {t('aiNews.noArticles')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {displayedArticles.map((article, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: alpha('#1976d2', 0.3),
                      transform: 'translateY(-1px)',
                      boxShadow: `0 2px 8px ${alpha('#000', 0.08)}`
                    }
                  }}
                >
                  {/* Title */}
                  <MuiLink
                    href={article.url}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      display: 'block',
                      mb: 1,
                      lineHeight: 1.4,
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    {article.title}
                  </MuiLink>

                  {/* Description */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 1.5
                    }}
                  >
                    {article.body}
                  </Typography>

                  {/* Meta Info */}
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    {article.provider_name && (
                      <Chip 
                        label={article.provider_name}
                        size="small" 
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: getProviderColor(article.provider_name).bg,
                          color: getProviderColor(article.provider_name).text,
                          border: '1px solid',
                          borderColor: getProviderColor(article.provider_name).border
                        }}
                      />
                    )}
                    <Chip 
                      label={article.source} 
                      size="small" 
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        bgcolor: alpha('#000', 0.04),
                        border: '1px solid',
                        borderColor: alpha('#000', 0.08)
                      }}
                    />
                    {article.sentiment && (
                      <Chip
                        label={article.sentiment.sentiment}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: article.sentiment.sentiment === 'positive' ? alpha('#4caf50', 0.1)
                            : article.sentiment.sentiment === 'negative' ? alpha('#f44336', 0.1)
                            : alpha('#ff9800', 0.1),
                          color: article.sentiment.sentiment === 'positive' ? 'success.dark'
                            : article.sentiment.sentiment === 'negative' ? 'error.dark'
                            : 'warning.dark',
                          border: '1px solid',
                          borderColor: article.sentiment.sentiment === 'positive' ? alpha('#4caf50', 0.3)
                            : article.sentiment.sentiment === 'negative' ? alpha('#f44336', 0.3)
                            : alpha('#ff9800', 0.3)
                        }}
                      />
                    )}
                    {article.published_date && (
                      <>
                        <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {new Date(article.published_date).toLocaleDateString()}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        {/* Load More Button */}
        {hasMore && (
          <Box 
            px={2} 
            pb={2}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha('#000', 0.015)
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ExpandMoreIcon />}
              onClick={handleLoadMore}
              sx={{
                mt: 2,
                py: 1,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                borderColor: alpha('#1976d2', 0.3),
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha('#1976d2', 0.04)
                }
              }}
            >
              {t('aiNews.loadMore')} ({articles.length - displayCount} {t('aiNews.remaining')})
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RecentArticlesList;

